import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companyService } from '../services/companyService';
import { useAuth } from './AuthContext';

export type ThemeType = 'blue' | 'green' | 'purple' | 'red' | 'orange';

interface Theme {
  name: string;
  label: string;
  colors: {
    primary: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
      950: string;
    };
  };
}

const themes: Record<ThemeType, Theme> = {
  blue: {
    name: 'blue',
    label: 'Azul',
    colors: {
      primary: {
        50: '#eff6ff',
        100: '#dbeafe',
        200: '#bfdbfe',
        300: '#93c5fd',
        400: '#60a5fa',
        500: '#3b82f6',
        600: '#2563eb',
        700: '#1d4ed8',
        800: '#1e40af',
        900: '#1e3a8a',
        950: '#172554',
      },
    },
  },
  green: {
    name: 'green',
    label: 'Verde',
    colors: {
      primary: {
        50: '#f0fdf4',
        100: '#dcfce7',
        200: '#bbf7d0',
        300: '#86efac',
        400: '#4ade80',
        500: '#22c55e',
        600: '#16a34a',
        700: '#15803d',
        800: '#166534',
        900: '#14532d',
        950: '#052e16',
      },
    },
  },
  purple: {
    name: 'purple',
    label: 'Morado',
    colors: {
      primary: {
        50: '#faf5ff',
        100: '#f3e8ff',
        200: '#e9d5ff',
        300: '#d8b4fe',
        400: '#c084fc',
        500: '#a855f7',
        600: '#9333ea',
        700: '#7c3aed',
        800: '#6b21a8',
        900: '#581c87',
        950: '#3b0764',
      },
    },
  },
  red: {
    name: 'red',
    label: 'Rojo',
    colors: {
      primary: {
        50: '#fef2f2',
        100: '#fee2e2',
        200: '#fecaca',
        300: '#fca5a5',
        400: '#f87171',
        500: '#ef4444',
        600: '#dc2626',
        700: '#b91c1c',
        800: '#991b1b',
        900: '#7f1d1d',
        950: '#450a0a',
      },
    },
  },
  orange: {
    name: 'orange',
    label: 'Naranja',
    colors: {
      primary: {
        50: '#fff7ed',
        100: '#ffedd5',
        200: '#fed7aa',
        300: '#fdba74',
        400: '#fb923c',
        500: '#f97316',
        600: '#ea580c',
        700: '#c2410c',
        800: '#9a3412',
        900: '#7c2d12',
        950: '#431407',
      },
    },
  },
};

interface ThemeContextType {
  currentTheme: ThemeType;
  theme: Theme;
  setTheme: (theme: ThemeType) => void;
  availableThemes: Theme[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [currentTheme, setCurrentTheme] = useState<ThemeType>('blue');
  const queryClient = useQueryClient();

  // Load theme from company settings
  const { data: companySettings } = useQuery({
    queryKey: ['company-settings'],
    queryFn: companyService.getCompanySettings,
    enabled: isAuthenticated,
  });

  // Update currentTheme when company settings change
  useEffect(() => {
    if (companySettings?.theme && themes[companySettings.theme as ThemeType]) {
      setCurrentTheme(companySettings.theme as ThemeType);
    }
  }, [companySettings?.theme]);

  // Apply theme by injecting CSS
  useEffect(() => {
    const theme = themes[currentTheme];

    // Remove existing theme style tag
    const existingStyle = document.getElementById('theme-styles');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Create new style tag with theme colors
    const style = document.createElement('style');
    style.id = 'theme-styles';
    style.textContent = `
      :root {
        --color-primary-50: ${theme.colors.primary[50]};
        --color-primary-100: ${theme.colors.primary[100]};
        --color-primary-200: ${theme.colors.primary[200]};
        --color-primary-300: ${theme.colors.primary[300]};
        --color-primary-400: ${theme.colors.primary[400]};
        --color-primary-500: ${theme.colors.primary[500]};
        --color-primary-600: ${theme.colors.primary[600]};
        --color-primary-700: ${theme.colors.primary[700]};
        --color-primary-800: ${theme.colors.primary[800]};
        --color-primary-900: ${theme.colors.primary[900]};
        --color-primary-950: ${theme.colors.primary[950]};
      }
    `;

    document.head.appendChild(style);
  }, [currentTheme]);

  // Mutation to update theme in database
  const updateThemeMutation = useMutation({
    mutationFn: (theme: ThemeType) =>
      companyService.updateCompanySettings({ theme }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
      if (data.theme) {
        setCurrentTheme(data.theme as ThemeType);
      }
    }
  });

  const setTheme = (theme: ThemeType) => {
    // Update locally immediately for better UX
    setCurrentTheme(theme);
    // Save to database
    updateThemeMutation.mutate(theme);
  };

  const value: ThemeContextType = {
    currentTheme,
    theme: themes[currentTheme],
    setTheme,
    availableThemes: Object.values(themes),
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};